/**
 * Controlled-vocabulary option lists for the §1B vendor apply form (and the §9
 * wizard edit-section forms). Slugs are stable wire values; labels come from
 * i18n keys so the form stays EN/ES. The store-type taxonomy lives in its own
 * file ([[storeTypes]]); everything else the form captures is here.
 */

export interface Option {
  slug: string;
  labelKey: string;
}

/** Contact's role at the business (§1B Business section). */
export const CONTACT_ROLES: Option[] = [
  { slug: 'owner', labelKey: 'applyOpts.role.owner' },
  { slug: 'manager', labelKey: 'applyOpts.role.manager' },
  { slug: 'staff', labelKey: 'applyOpts.role.staff' },
  { slug: 'other', labelKey: 'applyOpts.role.other' },
];

/** Number of store locations (§1B Business section). */
export const LOCATION_COUNTS: Option[] = [
  { slug: '1', labelKey: 'applyOpts.locations.1' },
  { slug: '2', labelKey: 'applyOpts.locations.2' },
  { slug: '3', labelKey: 'applyOpts.locations.3' },
  { slug: '4', labelKey: 'applyOpts.locations.4' },
  { slug: '5', labelKey: 'applyOpts.locations.5' },
  { slug: '6-10', labelKey: 'applyOpts.locations.6-10' },
  { slug: '11-15', labelKey: 'applyOpts.locations.11-15' },
  { slug: '16-20', labelKey: 'applyOpts.locations.16-20' },
  { slug: '21+', labelKey: 'applyOpts.locations.21+' },
];

/** Social platforms for the repeater (§1B Business section). */
export const SOCIAL_PLATFORMS: Option[] = [
  { slug: 'instagram', labelKey: 'applyOpts.social.instagram' },
  { slug: 'facebook', labelKey: 'applyOpts.social.facebook' },
  { slug: 'tiktok', labelKey: 'applyOpts.social.tiktok' },
  { slug: 'x', labelKey: 'applyOpts.social.x' },
  { slug: 'youtube', labelKey: 'applyOpts.social.youtube' },
  { slug: 'other', labelKey: 'applyOpts.social.other' },
];

/** Point-of-sale systems (§1B Systems & eligibility section). */
export const POS_SYSTEMS: Option[] = [
  { slug: '', labelKey: 'applyOpts.pos.none' },
  { slug: 'square', labelKey: 'applyOpts.pos.square' },
  { slug: 'toast', labelKey: 'applyOpts.pos.toast' },
  { slug: 'clover', labelKey: 'applyOpts.pos.clover' },
  { slug: 'shopify', labelKey: 'applyOpts.pos.shopify' },
  { slug: 'other', labelKey: 'applyOpts.pos.other' },
];

/** Approximate product count (§1B Your Interests section). */
export const PRODUCT_COUNTS: Option[] = [
  { slug: '<100', labelKey: 'applyOpts.productCount.lt100' },
  { slug: '100-500', labelKey: 'applyOpts.productCount.100-500' },
  { slug: '500+', labelKey: 'applyOpts.productCount.500plus' },
];

/** "What matters most to you" chips (§1B Your Interests section). */
export const INTEREST_OTHER = 'other';
export const INTERESTS: Option[] = [
  { slug: 'local_exposure', labelKey: 'applyOpts.interest.local_exposure' },
  { slug: 'online_ordering', labelKey: 'applyOpts.interest.online_ordering' },
  { slug: 'delivery', labelKey: 'applyOpts.interest.delivery' },
  { slug: 'win_basket', labelKey: 'applyOpts.interest.win_basket' },
  { slug: 'coupons', labelKey: 'applyOpts.interest.coupons' },
  { slug: 'loyalty', labelKey: 'applyOpts.interest.loyalty' },
  { slug: 'recall_alerts', labelKey: 'applyOpts.interest.recall_alerts' },
  { slug: INTEREST_OTHER, labelKey: 'applyOpts.interest.other' },
];

/** Days of the week for the store-hours grid (Mon–Sun), stable wire keys. */
export const WEEK_DAYS: Option[] = [
  { slug: 'monday', labelKey: 'applyOpts.day.monday' },
  { slug: 'tuesday', labelKey: 'applyOpts.day.tuesday' },
  { slug: 'wednesday', labelKey: 'applyOpts.day.wednesday' },
  { slug: 'thursday', labelKey: 'applyOpts.day.thursday' },
  { slug: 'friday', labelKey: 'applyOpts.day.friday' },
  { slug: 'saturday', labelKey: 'applyOpts.day.saturday' },
  { slug: 'sunday', labelKey: 'applyOpts.day.sunday' },
];

/**
 * Starter certifications list for the §9 Storefront step (NOT yet locked — the
 * curated vocab is a separate Rod-driven pass). `formal` certs require proof and
 * show a Verified badge once confirmed; the rest are self-declared.
 */
export interface CertOption {
  slug: string;
  labelKey: string;
  formal: boolean;
}
export const CERTIFICATIONS: CertOption[] = [
  { slug: 'usda_organic', labelKey: 'applyOpts.cert.usda_organic', formal: true },
  { slug: 'usda_inspected', labelKey: 'applyOpts.cert.usda_inspected', formal: true },
  { slug: 'halal', labelKey: 'applyOpts.cert.halal', formal: true },
  { slug: 'kosher', labelKey: 'applyOpts.cert.kosher', formal: true },
  { slug: 'non_gmo', labelKey: 'applyOpts.cert.non_gmo', formal: true },
  { slug: 'gluten_free', labelKey: 'applyOpts.cert.gluten_free', formal: true },
  { slug: 'fair_trade', labelKey: 'applyOpts.cert.fair_trade', formal: true },
  { slug: 'animal_welfare', labelKey: 'applyOpts.cert.animal_welfare', formal: true },
  { slug: 'woman_owned', labelKey: 'applyOpts.cert.woman_owned', formal: false },
  { slug: 'minority_owned', labelKey: 'applyOpts.cert.minority_owned', formal: false },
  { slug: 'veteran_owned', labelKey: 'applyOpts.cert.veteran_owned', formal: false },
  { slug: 'locally_owned', labelKey: 'applyOpts.cert.locally_owned', formal: false },
];
